import React, { useState, useEffect, useMemo } from 'react';
import {
  Typography, 
  Paper, 
  Box, 
  Button, 
  ButtonGroup,
  CircularProgress, 
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
} from '@mui/material';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import AddIcon from '@mui/icons-material/Add';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import AssessmentIcon from '@mui/icons-material/Assessment';
import HistoryIcon from '@mui/icons-material/History';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import SettingsIcon from '@mui/icons-material/Settings';
import DeleteIcon from '@mui/icons-material/Delete';
import TableBarIcon from '@mui/icons-material/TableBar';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import ownAuthSession from '../../../services/auth/ownAuthSession';
import firebaseDataService from '../../../services/FirebaseDataService';
import { getBranches as getBranchesAdapter } from '../../../services/migration/branchDataService';
import AddRestaurantSetupModal from './AddRestaurantSetupModal';
import { useAuth } from '../../../contexts/AuthContext';
import QuickSaleScreenSettingsModal from '../quickSale/components/modals/QuickSaleScreenSettingsModal';
import { restaurantScreenApi, RestaurantScreenDto } from '../../../services/restaurantApiService';
import salesService from '../../../services/salesService';
import { useTranslation } from 'react-i18next';
import useSalesScreensPageAgentBridge from '../hooks/useSalesScreensPageAgentBridge';
import BranchSearchFilterBar, { UNASSIGNED_BRANCH_ID } from '../../common/BranchSearchFilterBar';

interface RestaurantSetupData {
  id: number; // Artık SQL'den geliyor, INT
  name: string;
  branchId?: string | null;
  branchName?: string; // Firebase'den alınan şube adı
  quickSaleScreenId: number;
  createdAt?: string; // API'den ISO string olarak geliyor
}

const HeaderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  padding: theme.spacing(3),
  overflowY: 'auto', 
  height: 'calc(100vh - 64px)',
  '&::-webkit-scrollbar': {
    width: '8px',
  },
  '&::-webkit-scrollbar-track': {
    background: '#f1f1f1',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb': {
    background: '#888',
    borderRadius: '4px',
  },
  '&::-webkit-scrollbar-thumb:hover': {
    background: '#555',
  },
}));

const RestaurantPage: React.FC = () => {
  const { t } = useTranslation('salesSystem');
  const navigate = useNavigate();
  const { getBranchName, branches } = useAuth(); // ✅ AuthContext'ten şube adı alma fonksiyonu ve branches
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [restaurantSetups, setRestaurantSetups] = useState<RestaurantSetupData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isSettingsModalOpen, setIsSettingsModalOpen] = useState(false);
  const [selectedSetupForSettings, setSelectedSetupForSettings] = useState<RestaurantSetupData | null>(null);
  const [setupToDelete, setSetupToDelete] = useState<RestaurantSetupData | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletingSetup, setDeletingSetup] = useState(false);
  
  // Raporlar flyout menüsü
  const [reportsMenuAnchor, setReportsMenuAnchor] = useState<null | HTMLElement>(null);
  
  // ✅ YENİ: Satır işlemleri menüsü
  const [rowMenuAnchor, setRowMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedRowSetup, setSelectedRowSetup] = useState<RestaurantSetupData | null>(null);
  
  // ✅ YENİ: Düzenleme modalı
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [setupToEdit, setSetupToEdit] = useState<RestaurantSetupData | null>(null);

  // Şube + ekran adı filtresi
  const [filterBranchId, setFilterBranchId] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState('');

  const filteredSetups = useMemo(() => {
    const term = filterSearch.trim().toLocaleLowerCase('tr');
    return restaurantSetups.filter((setup) => {
      if (filterBranchId === UNASSIGNED_BRANCH_ID) {
        if (setup.branchId) return false;
      } else if (filterBranchId && setup.branchId !== filterBranchId) {
        return false;
      }
      if (!term) return true;
      return (setup.name || '').toLocaleLowerCase('tr').includes(term);
    });
  }, [restaurantSetups, filterBranchId, filterSearch]);

  
  const handleOpenRowMenu = (event: React.MouseEvent<HTMLElement>, setup: RestaurantSetupData) => {
    setRowMenuAnchor(event.currentTarget);
    setSelectedRowSetup(setup);
  };
  
  const handleCloseRowMenu = () => {
    setRowMenuAnchor(null);
    setSelectedRowSetup(null);
  };
  
  // ✅ YENİ: Düzenleme modalı handler'ları
  const handleOpenEditModal = (setup: RestaurantSetupData) => {
    setSetupToEdit(setup);
    setIsEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setSetupToEdit(null);
    setIsEditModalOpen(false);
  };

  // ✅ YENİ: Restoran ekranları listesini yenile
  const refreshRestaurantList = async () => {
    if (!companyId) return;
    
    try {
      const screens = await restaurantScreenApi.getByCompanyId();
      const list: RestaurantSetupData[] = screens.map(screen => ({
        id: screen.Id,
        name: screen.Name || t('restaurantPage.unnamedSetup'),
        branchId: screen.BranchId,
        branchName: getBranchName(screen.BranchId),
        quickSaleScreenId: screen.QuickSaleScreenId,
        createdAt: screen.CreatedAt,
      }));

      setRestaurantSetups(list.sort((a, b) => 
        new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
      ));
    } catch (error) {
      console.error("Restoran ekranları yenilenirken hata:", error);
    }
  };

  useEffect(() => {
    const uid = ownAuthSession.getCurrentUid();
    if (uid) {
      firebaseDataService.getUserData(uid).then((userData) => {
        if (userData) {
          setCompanyId(userData.companyId ?? null);
        } else {
          setError(t('restaurantPage.errors.userCompanyNotFound'));
          setLoading(false);
        }
      }).catch((err) => {
        console.error("Şirket ID'si alınırken hata:", err);
        setError(t('restaurantPage.errors.companyIdFetchFailed'));
        setLoading(false);
      });
    } else {
      setError(t('restaurantPage.errors.noLoggedInUser'));
      navigate('/login');
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (!companyId) {
      // ✅ FIX: Sadece loading durumunu kontrol et
      if (loading) setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    // API'den restoran ekranlarını çek
    restaurantScreenApi.getByCompanyId()
      .then((screens: RestaurantScreenDto[]) => {
        // ✅ AuthContext'ten getBranchName ile şube adlarını al
        const list: RestaurantSetupData[] = screens.map(screen => ({
          id: screen.Id,
          name: screen.Name || t('restaurantPage.unnamedSetup'),
          branchId: screen.BranchId,
          branchName: getBranchName(screen.BranchId), // ✅ AuthContext'ten şube adı
          quickSaleScreenId: screen.QuickSaleScreenId,
          createdAt: screen.CreatedAt,
        }));

        // CreatedAt'e göre sırala (yeniden eskiye)
        setRestaurantSetups(list.sort((a, b) => 
          new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
        ));
        
        setLoading(false);
      })
      .catch((err) => {
        console.error("Restoran ekranları yüklenirken hata:", err);
        setError(t('restaurantPage.errors.listLoadFailed') + ': ' + (err.response?.data?.Message || err.message));
        setLoading(false);
      });
  }, [companyId, branches, getBranchName]); // ✅ branches ve getBranchName eklendi (şube adlarının doğru görünmesi için)


  const handleOpenAddModal = () => {
    setIsAddModalOpen(true);
  };

  const handleCloseAddModal = () => {
    setIsAddModalOpen(false);
  };


  const handleModalClose = async () => {
    handleCloseAddModal();
    // Modal başarılı bir şekilde kaydettiyse, liste otomatik güncellenecek
    // Listeyi manuel yenilemek için:
    if (companyId) {
      try {
        const screens = await restaurantScreenApi.getByCompanyId();
        const list: RestaurantSetupData[] = screens.map(screen => ({
          id: screen.Id,
          name: screen.Name || t('restaurantPage.unnamedSetup'),
          branchId: screen.BranchId,
          branchName: undefined,
          quickSaleScreenId: screen.QuickSaleScreenId,
          createdAt: screen.CreatedAt,
        }));

        // Şube bilgilerini Firebase'den çek
        if (list.length > 0) {
          const screensWithBranch = list.filter(screen => screen.branchId);
          
          if (screensWithBranch.length > 0) {
            // Subeler SQL-once (branchDataService); id = firebaseId (screen.branchId ile uyumlu).
            const serviceBranches = await getBranchesAdapter(companyId);
            const branchesData: Record<string, any> = {};
            serviceBranches.forEach((b) => { branchesData[b.id] = b; });

            const updatedList = list.map(screen => {
              if (screen.branchId && branchesData[screen.branchId]) {
                return {
                  ...screen,
                  branchName: branchesData[screen.branchId].name || t('restaurantPage.unnamedBranch')
                };
              }
              return screen;
            });

            setRestaurantSetups(updatedList.sort((a, b) => 
              new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
            ));
          } else {
            setRestaurantSetups(list.sort((a, b) => 
              new Date(a.createdAt || 0).getTime() - new Date(b.createdAt || 0).getTime()
            ));
          }
        } else {
          setRestaurantSetups([]);
        }
      } catch (err) {
        console.error("Liste yenilenirken hata:", err);
      }
    }
  };
  
  const handleGoToScreen = (setupId: number) => {
    navigate(`/restaurant-screen/${setupId}`);
  };

  const handleOpenSettingsModal = (setup: RestaurantSetupData) => {
    setSelectedSetupForSettings(setup);
    setIsSettingsModalOpen(true);
  };

  const handleCloseSettingsModal = () => {
    setSelectedSetupForSettings(null);
    setIsSettingsModalOpen(false);
  };

  // AI ajan köprüsü: sayfa açıkken ajan restoran ekranlarını listeleyebilir,
  // ayar modalını açabilir ve yeni ekran sonrası listeyi yenileyebilir.
  useSalesScreensPageAgentBridge('restaurant', {
    screens: restaurantSetups.map((s) => ({
      id: String(s.id),
      name: s.name,
      branchName: s.branchName ?? null,
    })),
    openSettings: (screen) => {
      const full = restaurantSetups.find((s) => String(s.id) === screen.id);
      if (full) handleOpenSettingsModal(full);
    },
    refreshScreens: () => refreshRestaurantList(),
  });

  const handleOpenDeleteModal = (setup: RestaurantSetupData) => {
    setSetupToDelete(setup);
    setIsDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setSetupToDelete(null);
    setIsDeleteModalOpen(false);
  };

  const handleDeleteSetup = async () => {
    if (!setupToDelete || !companyId) {
      setError(t('restaurantPage.errors.deleteInfoMissing'));
      return;
    }
    setDeletingSetup(true);
    setError(null);

    try {
      // QuickSaleScreen'i sil → CASCADE ile RestaurantScreen otomatik silinir
      await salesService.deleteQuickSaleScreen(setupToDelete.quickSaleScreenId);
      
      console.log(`✅ Restoran ekranı silindi: ${setupToDelete.name} (QuickSaleScreenId: ${setupToDelete.quickSaleScreenId})`);
      
      // Listeyi güncelle
      setRestaurantSetups(prevSetups => prevSetups.filter(s => s.id !== setupToDelete.id));
      
      handleCloseDeleteModal();
    } catch (error) {
      console.error("❌ Restoran ekranı silinirken hata:", error);
      setError(`${t('restaurantPage.errors.deleteFailed')}: ${error instanceof Error ? error.message : t('restaurantPage.unknownError')}`);
    } finally {
      setDeletingSetup(false);
    }
  };


  const pageLoading = loading && !companyId && !error;

  if (pageLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="calc(100vh - 64px)">
        <CircularProgress />
        <Typography sx={{ml: 2}}>{t('restaurantPage.loadingCompany')}</Typography>
                  </Box>
    );
  }
  
  return (
    <ScrollableContent>
          <HeaderContainer sx={{ flexDirection: { xs: 'column', md: 'row' }, alignItems: { xs: 'flex-start', md: 'center' }, gap: { xs: 2, md: 0 } }}>
            <Box sx={{ display: 'flex', alignItems: 'center', width: { xs: '100%', md: 'auto' } }}>
              <RestaurantIcon sx={{ fontSize: 32, mr: 1.5, color: 'primary.main' }} />
              <Typography variant="h5" component="h1">
                {t('restaurantPage.title')}
              </Typography>
            </Box>
            {/* Desktop Butonlar */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, justifyContent: 'flex-end' }}>
              <ButtonGroup>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={handleOpenAddModal}
                  disabled={!companyId}
                >
                  {t('restaurantPage.actions.newRestaurantScreen')}
                </Button>
                <Button
                  variant="outlined"
                  onClick={(e) => setReportsMenuAnchor(e.currentTarget)}
                  sx={{ minWidth: 'auto', px: 1 }}
                >
                  <MoreVertIcon />
                </Button>
              </ButtonGroup>
              
              {/* 3 Nokta Menüsü */}
              <Menu
                anchorEl={reportsMenuAnchor}
                open={Boolean(reportsMenuAnchor)}
                onClose={() => setReportsMenuAnchor(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <MenuItem onClick={() => { navigate('/restaurant-orders'); setReportsMenuAnchor(null); }}>
                  <ListItemIcon>
                    <ReceiptLongIcon fontSize="small" color="info" />
                  </ListItemIcon>
                  <ListItemText>{t('restaurantPage.actions.orderReports')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { navigate('/gun-sonu'); setReportsMenuAnchor(null); }}>
                  <ListItemIcon>
                    <AssessmentIcon fontSize="small" color="primary" />
                  </ListItemIcon>
                  <ListItemText>{t('restaurantPage.actions.endOfDayReport')}</ListItemText>
                </MenuItem>
                <MenuItem onClick={() => { navigate('/gun-sonlari'); setReportsMenuAnchor(null); }}>
                  <ListItemIcon>
                    <HistoryIcon fontSize="small" color="secondary" />
                  </ListItemIcon>
                  <ListItemText>{t('restaurantPage.actions.endOfDayList')}</ListItemText>
                </MenuItem>
              </Menu>
            </Box>
            
            {/* Mobil Butonlar */}
            <Box sx={{ display: { xs: 'flex', md: 'none' }, gap: 1, width: '100%' }}>
              <Button 
                fullWidth
                variant="contained" 
                color="primary"
                startIcon={<AddIcon />}
                onClick={handleOpenAddModal}
                disabled={!companyId}
                size="small"
              >
                {t('restaurantPage.actions.newRestaurantScreen')}
              </Button>
              <Button
                variant="outlined"
                onClick={(e) => setReportsMenuAnchor(e.currentTarget)}
                size="small"
                sx={{ minWidth: 'auto', px: 1 }}
              >
                <MoreVertIcon />
              </Button>
            </Box>
          </HeaderContainer>

          {loading && companyId && (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
              <CircularProgress />
              <Typography sx={{ml: 2}}>{t('restaurantPage.loadingScreens')}</Typography>
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
          )}

          {!loading && !error && restaurantSetups.length === 0 && companyId && (
            <Paper sx={{ p: 3, textAlign: 'center', mt: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                {t('restaurantPage.empty.title')}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                {t('restaurantPage.empty.description')}
              </Typography>
            </Paper>
          )}

          {!loading && !error && restaurantSetups.length > 0 && (
            <BranchSearchFilterBar
              branches={branches}
              branchId={filterBranchId}
              onBranchChange={setFilterBranchId}
              search={filterSearch}
              onSearchChange={setFilterSearch}
              searchLabel={t('restaurantPage.table.restaurantScreen')}
              shownCount={filteredSetups.length}
              totalCount={restaurantSetups.length}
            />
          )}

          {!loading && !error && restaurantSetups.length > 0 && filteredSetups.length === 0 && (
            <Paper sx={{ p: 3, textAlign: 'center', mt: 2 }}>
              <Typography color="text.secondary">
                {t('common2:listFilter.noResults')}
              </Typography>
            </Paper>
          )}

          {!loading && !error && filteredSetups.length > 0 && (
            <>
              {/* 📱 Mobile Card View (xs, sm) */}
              <Box sx={{ display: { xs: 'block', md: 'none' }, mt: 2 }}>
                {filteredSetups.map((setup) => (
                  <Paper 
                    key={setup.id}
                    elevation={2} 
                    sx={{ 
                      p: 2, 
                      mb: 2,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      '&:hover': { 
                        boxShadow: 4,
                        borderColor: 'primary.main'
                      }
                    }}
                  >
                    {/* Restoran Ekranı Adı */}
                    <Box display="flex" alignItems="center" gap={1} mb={1.5}>
                      <RestaurantIcon sx={{ fontSize: 24, color: 'primary.main' }} />
                      <Typography variant="h6" fontWeight="bold">
                        {setup.name}
                      </Typography>
                    </Box>

                    {/* Şube Bilgisi */}
                    <Box display="flex" justifyContent="space-between" mb={2}>
                      <Typography variant="body2" color="text.secondary">{t('restaurantPage.table.branch')}:</Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {setup.branchName || t('restaurantPage.branchUnassigned')}
                      </Typography>
                    </Box>

                    {/* İşlem Butonları */}
                    <Box display="flex" gap={1} alignItems="center">
                      <Button 
                        fullWidth
                        size="small" 
                        variant="outlined"
                        onClick={() => handleGoToScreen(setup.id)}
                        startIcon={<TableBarIcon />}
                      >
                        {t('restaurantPage.actions.tables')}
                      </Button>
                      <IconButton
                        size="small"
                        onClick={(e) => handleOpenRowMenu(e, setup)}
                        sx={{ 
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  </Paper>
                ))}
              </Box>

              {/* 🖥️ Desktop Table View (md+) */}
              <Paper elevation={3} sx={{ display: { xs: 'none', md: 'block' }, borderRadius: 2, overflow: 'hidden', mt: 2 }}>
                <TableContainer>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '40%' }}>{t('restaurantPage.table.restaurantScreen')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '20%' }}>{t('restaurantPage.table.branch')}</TableCell>
                        <TableCell sx={{ fontWeight: 'bold', bgcolor: 'primary.main', color: 'white', width: '40%', textAlign: 'right' }}>{t('restaurantPage.table.actions')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredSetups.map((setup) => (
                        <TableRow 
                          key={setup.id}
                          hover
                          sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                        >
                          <TableCell component="th" scope="row">
                            <Typography variant="subtitle1" fontWeight="medium">{setup.name}</Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {setup.branchName || t('restaurantPage.branchUnassigned')}
                            </Typography>
                          </TableCell>
                          <TableCell align="right" sx={{ p: '6px' }}> 
                            <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                              <Button 
                                size="small" 
                                variant="outlined"
                                onClick={() => handleGoToScreen(setup.id)}
                                startIcon={<TableBarIcon />}
                              >
                                {t('restaurantPage.actions.tables')}
                              </Button>
                              <IconButton
                                size="small"
                                onClick={(e) => handleOpenRowMenu(e, setup)}
                                sx={{ 
                                  border: '1px solid',
                                  borderColor: 'divider',
                                  borderRadius: 1,
                                }}
                              >
                                <MoreVertIcon />
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </>
          )}

      {companyId && (
        <AddRestaurantSetupModal
          open={isAddModalOpen}
          onClose={handleModalClose}
          companyId={companyId}
          onSaved={refreshRestaurantList}
        />
      )}

      {/* ✅ YENİ: Düzenleme Modalı */}
      {setupToEdit && companyId && (
        <AddRestaurantSetupModal
          open={isEditModalOpen}
          onClose={handleCloseEditModal}
          companyId={companyId}
          editMode={true}
          restaurantScreenId={setupToEdit.id}
          quickSaleScreenId={setupToEdit.quickSaleScreenId}
          screenName={setupToEdit.name}
          screenBranchId={setupToEdit.branchId}
          onSaved={refreshRestaurantList}
        />
      )}

      {/* Restoran Ayarları Modalı (QuickSaleScreenSettingsModal + Restoran Tabları) */}
      {selectedSetupForSettings && companyId && (
        <QuickSaleScreenSettingsModal
          open={isSettingsModalOpen}
          onClose={handleCloseSettingsModal}
          screenId={selectedSetupForSettings.quickSaleScreenId.toString()}
          companyId={companyId}
          branchId={selectedSetupForSettings.branchId}
          screenName={selectedSetupForSettings.name}
          isRestaurantMode={true}
          restaurantScreenId={selectedSetupForSettings.id}
          onSaveSuccess={() => {
            console.log('✅ Ayarlar kaydedildi');
          }}
        />
      )}

      {/* ✅ YENİ: Satır İşlemleri Menüsü */}
      <Menu
        anchorEl={rowMenuAnchor}
        open={Boolean(rowMenuAnchor)}
        onClose={handleCloseRowMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem 
          onClick={() => {
            if (selectedRowSetup) handleOpenEditModal(selectedRowSetup);
            handleCloseRowMenu();
          }}
        >
          <ListItemIcon>
            <EditIcon fontSize="small" color="primary" />
          </ListItemIcon>
          <ListItemText>{t('restaurantPage.actions.edit')}</ListItemText>
        </MenuItem>
        <MenuItem 
          onClick={() => {
            if (selectedRowSetup) handleOpenSettingsModal(selectedRowSetup);
            handleCloseRowMenu();
          }}
        >
          <ListItemIcon>
            <SettingsIcon fontSize="small" color="secondary" />
          </ListItemIcon>
          <ListItemText>{t('restaurantPage.actions.settings')}</ListItemText>
        </MenuItem>
        <Divider />
        <MenuItem 
          onClick={() => {
            if (selectedRowSetup) handleOpenDeleteModal(selectedRowSetup);
            handleCloseRowMenu();
          }}
          sx={{ color: 'error.main' }}
        >
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText>{t('restaurantPage.actions.delete')}</ListItemText>
        </MenuItem>
      </Menu>

      {/* Silme Onay Modalı */}
      <Dialog open={isDeleteModalOpen} onClose={handleCloseDeleteModal}>
        <DialogTitle>{t('restaurantPage.deleteDialog.title')}</DialogTitle>
        <DialogContent>
          <Typography>
            {t('restaurantPage.deleteDialog.description', { name: setupToDelete?.name })}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteModal} disabled={deletingSetup}>
            {t('restaurantPage.common.cancel')}
          </Button>
          <Button 
            onClick={handleDeleteSetup} 
            color="error" 
            variant="contained" 
            disabled={deletingSetup}
          >
            {deletingSetup ? <CircularProgress size={24} /> : t('restaurantPage.actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </ScrollableContent>
  );
};

export default RestaurantPage; 